import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import {
  REPLAY_AGAIN_LEAD,
  REPLAY_MODE_LABEL,
  REPLAY_MODE_SENTENCE,
  REPLAY_SOURCE_KIND_LABEL,
  replayOriginFact,
  replaySourceSize,
  replayStartedLabel,
} from '@/lib/replay-mark';
import type { SessionReplayFull } from '@matter_extractor/shared';
import { Link } from '@tanstack/react-router';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { type ReactNode, useId, useState } from 'react';

/**
 * The panel « Rejeu par Claude » of the parcours (rejeu.md §12, variante A of
 * the review of the maquettes, issue #193): between the head and the figures,
 * on a session Claude played in the place of a person - and mounted on no other.
 *
 * ## Why a panel and not a ninth tile
 *
 * « La nature de la session change la lecture de toute la page, et les tuiles
 * restent des chiffres » (maquettes/rejeu, README): that a session was played by
 * Claude is the first thing to know before reading its parcours, so it stands
 * above the figures, framed in the violet of the pill, and the eight tiles below
 * stay what they were.
 *
 * ## What it holds, in the order of the maquette
 *
 * The line « lancé par ... » (the operator, the instant, the declared model),
 * three facts - the Mode with its sentence, the Source with its kind and size,
 * and the replay this one replayed - then the two texts the session kept, each
 * UNFOLDED in place in a scrolling box rather than behind a page of their own,
 * and the sentence that says how to play the same case again, with the
 * identifier to copy.
 *
 * ## The identifier is the whole one
 *
 * `fromReplay` takes the identifier of a replay, which is its session's: the
 * eight first characters would be a name an operator can say aloud and a tool
 * could not resolve. It is drawn in fixed pitch and `select-all`, so a refused
 * clipboard (a plain-HTTP dev stack) still leaves it one click from Ctrl+C.
 */

export interface ReplayPanelProps {
  /** The identifier of the session, which is the identifier of the replay. */
  readonly sessionId: string;
  readonly replay: SessionReplayFull;
}

/** What « Copier » says after a press: the identifier is in the clipboard, or it could not be put there. */
type CopyState = 'idle' | 'copied' | 'failed';

export function ReplayPanel({ sessionId, replay }: ReplayPanelProps) {
  const titleId = useId();
  const sourceBoxId = useId();
  const instructionBoxId = useId();
  const [sourceOpen, setSourceOpen] = useState(false);
  const [instructionOpen, setInstructionOpen] = useState(false);
  const [copy, setCopy] = useState<CopyState>('idle');
  const origin = replayOriginFact(replay.fromReplay);

  async function copyId(): Promise<void> {
    try {
      await navigator.clipboard.writeText(sessionId);
      setCopy('copied');
    } catch {
      // No clipboard, or refused: the identifier stays selectable beside the button.
      setCopy('failed');
    }
    setTimeout(() => setCopy('idle'), 2000);
  }

  return (
    <section
      data-testid={SESSIONS_TEST_IDS.replayPanel}
      data-mode={replay.mode}
      data-source-kind={replay.sourceKind}
      aria-labelledby={titleId}
      className="flex flex-col gap-3 rounded-md border-2 border-violet-deep/25 bg-violet-tint/40 px-4.5 py-4"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 id={titleId} className="font-bold text-lg text-violet-deep leading-6">
          Rejeu par Claude
        </h2>
        <p data-testid={SESSIONS_TEST_IDS.replayStarted} className="text-gray-600 text-sm">
          lancé par <b className="font-semibold text-gray-800">{replay.operator}</b> le{' '}
          {replayStartedLabel(replay.startedAt)}, depuis l’assistant MCP
          {replay.playerModel === null ? null : ` · ${replay.playerModel} (déclaré)`}
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <Fact testId={SESSIONS_TEST_IDS.replayMode} label="Mode">
          <b className="font-semibold text-base text-gray-900">{REPLAY_MODE_LABEL[replay.mode]}</b>
          <span className="text-gray-600 text-xs">{REPLAY_MODE_SENTENCE[replay.mode]}</span>
        </Fact>
        <Fact testId={SESSIONS_TEST_IDS.replaySource} label="Source">
          <b className="break-words font-semibold text-base text-gray-900">
            « {replay.sourceLabel} »
          </b>
          <span className="text-gray-600 text-xs">
            {REPLAY_SOURCE_KIND_LABEL[replay.sourceKind]} · {replaySourceSize(replay.sourceChars)}
          </span>
        </Fact>
        <Fact testId={SESSIONS_TEST_IDS.replayOrigin} label="Rejoue" kind={origin.kind}>
          {origin.kind === 'linked' && origin.replayId !== null ? (
            <Link
              to="/admin/sessions/$sessionId"
              params={{ sessionId: origin.replayId }}
              data-testid={SESSIONS_TEST_IDS.replayOriginLink}
              className="font-semibold text-base text-bleu-nuit hover:underline"
            >
              {origin.label}
            </Link>
          ) : (
            <b
              className={`font-semibold text-base ${origin.kind === 'none' ? 'text-gray-500' : 'text-gray-900'}`}
            >
              {origin.label}
            </b>
          )}
          {origin.detail === null ? null : (
            <span className="text-gray-600 text-xs">{origin.detail}</span>
          )}
        </Fact>
      </div>

      <div className="flex flex-wrap gap-2">
        <Unfold
          testId={SESSIONS_TEST_IDS.replaySourceToggle}
          open={sourceOpen}
          controls={sourceBoxId}
          openLabel="Masquer la source"
          closedLabel="Voir la source"
          onToggle={() => setSourceOpen((open) => !open)}
        />
        <Unfold
          testId={SESSIONS_TEST_IDS.replayInstructionToggle}
          open={instructionOpen}
          controls={instructionBoxId}
          openLabel="Masquer la consigne servie"
          closedLabel="Voir la consigne servie"
          onToggle={() => setInstructionOpen((open) => !open)}
        />
      </div>

      {sourceOpen ? (
        <TextBox
          id={sourceBoxId}
          testId={SESSIONS_TEST_IDS.replaySourceText}
          label={`Source du rejeu : ${replay.sourceLabel}`}
          text={replay.sourceText}
        />
      ) : null}
      {instructionOpen ? (
        <div className="flex flex-col gap-1">
          <p className="text-gray-600 text-xs">
            Consigne telle que servie à Claude · empreinte{' '}
            <span className="font-mono text-gray-800">{replay.instructionFingerprint}</span>
          </p>
          <TextBox
            id={instructionBoxId}
            testId={SESSIONS_TEST_IDS.replayInstructionText}
            label="Consigne servie à Claude"
            text={replay.instruction}
          />
        </div>
      ) : null}

      <p
        data-testid={SESSIONS_TEST_IDS.replayAgain}
        className="flex flex-wrap items-baseline gap-x-1.5 text-gray-600 text-xs"
      >
        <span>{REPLAY_AGAIN_LEAD}</span>
        <span
          data-testid={SESSIONS_TEST_IDS.replayId}
          className="select-all break-all font-mono text-gray-800"
        >
          {sessionId}
        </span>
        <button
          type="button"
          data-testid={SESSIONS_TEST_IDS.replayCopy}
          data-state={copy}
          onClick={() => void copyId()}
          className="cursor-pointer font-semibold text-bleu-nuit underline underline-offset-3 hover:text-bleu-nuit-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {copy === 'copied' ? 'Copié' : copy === 'failed' ? 'Copie impossible' : 'Copier'}
        </button>
        <span aria-live="polite" className="sr-only">
          {copy === 'copied' ? 'Identifiant copié.' : null}
          {copy === 'failed'
            ? 'Le presse-papiers est indisponible : l’identifiant est à sélectionner.'
            : null}
        </span>
      </p>
    </section>
  );
}

/** One of the three facts: a small caps label, a value, and what qualifies it. */
function Fact({
  testId,
  label,
  kind,
  children,
}: {
  testId: string;
  label: string;
  /** The shape of the fact « Rejoue », for a suite to read. */
  kind?: string;
  children: ReactNode;
}) {
  return (
    <div data-testid={testId} data-kind={kind} className="flex min-w-0 flex-col gap-0.5">
      <span className="font-semibold text-gray-600 text-xs uppercase tracking-wide">{label}</span>
      {children}
    </div>
  );
}

/** « Voir la source » / « Masquer la source »: a disclosure, so `aria-expanded` and the id of what it opens. */
function Unfold({
  testId,
  open,
  controls,
  openLabel,
  closedLabel,
  onToggle,
}: {
  testId: string;
  open: boolean;
  controls: string;
  openLabel: string;
  closedLabel: string;
  onToggle: () => void;
}) {
  const Chevron = open ? ChevronUp : ChevronDown;
  return (
    <button
      type="button"
      data-testid={testId}
      aria-expanded={open}
      aria-controls={open ? controls : undefined}
      onClick={onToggle}
      className={`inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-md border-2 bg-white px-3 font-semibold text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
        open
          ? 'border-violet-deep text-violet-deep'
          : 'border-gray-200 text-gray-700 hover:border-gray-300'
      }`}
    >
      {open ? openLabel : closedLabel}
      <Chevron aria-hidden="true" className="size-3.5" strokeWidth={2} />
    </button>
  );
}

/**
 * A text unfolded in place, in a box that scrolls.
 *
 * Focusable and named, so the keyboard reaches it: a scrolling region nobody can
 * tab to is a text only a mouse can finish reading. `whitespace-pre-wrap`,
 * because a source is kept as Claude received it - its paragraphs are the
 * person's.
 */
function TextBox({
  id,
  testId,
  label,
  text,
}: {
  id: string;
  testId: string;
  label: string;
  text: string;
}) {
  return (
    <section
      id={id}
      data-testid={testId}
      aria-label={label}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: the rule guards against tab stops that do nothing; this one scrolls.
      tabIndex={0}
      className="max-h-40 overflow-auto whitespace-pre-wrap break-words rounded-md border border-gray-200 bg-white px-3 py-2.5 text-gray-800 text-sm leading-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {text}
    </section>
  );
}
